/**
 * Tokens de visualização de dados.
 *
 * A paleta categórica NÃO foi escolhida por gosto — foi validada. A anterior
 * (`#1976d2, #26a69a, #ef6c00, #7b1fa2, #c62828, #2e7d32, #455a64, #f9a825`)
 * reprovava em quatro verificações, e a pior era grave:
 *
 *   #2e7d32 ↔ #c62828 — ΔE 4,2 em deuteranopia
 *
 * Verde "ganho" e vermelho "perdido", lado a lado, indistinguíveis para cerca
 * de 8% dos homens. Num CRM isso não é detalhe estético: é o gráfico mentindo
 * para parte dos usuários.
 *
 * Esta paleta passa nas cinco verificações (faixa de luminosidade, piso de
 * croma, separação sob daltonismo, piso de visão normal e contraste contra a
 * superfície) — no tema CLARO e no ESCURO, com os mesmos valores.
 *
 * A ordem importa: o validador mede pares ADJACENTES, e verde e vermelho
 * ficaram deliberadamente afastados na sequência. Trocar a ordem invalida o
 * resultado — revalide antes de mexer.
 */

/** Ordem fixa. Nunca cicle: a 9ª série vira "Outros", não uma cor nova. */
export const categorical = [
  "#2563EB", // azul
  "#EA580C", // laranja
  "#0D9488", // teal
  "#9333EA", // roxo
  "#DC2626", // vermelho
  "#0891B2", // ciano
  "#65A30D", // verde
  "#A16207", // âmbar
];

/**
 * Cores de estado — reservadas.
 *
 * Nunca reaproveitadas como "série 4": se verde significa "bom" num gráfico e
 * "plano Enterprise" em outro, o leitor deixa de confiar na cor. Sempre
 * acompanhadas de rótulo, nunca sozinhas.
 */
export const status = {
  good: "#15803D",
  warning: "#B45309",
  serious: "#C2410C",
  critical: "#B91C1C",
  neutral: "#64748B",
};

/** Rampa sequencial: um matiz só, claro → escuro. Para magnitude. */
export const sequential = [
  "#DBEAFE",
  "#BFDBFE",
  "#93C5FD",
  "#60A5FA",
  "#3B82F6",
  "#2563EB",
  "#1D4ED8",
  "#1E40AF",
];

/**
 * Divergente: dois polos e um cinza no meio.
 *
 * O meio é neutro de propósito. Um matiz no ponto médio faria "zero" parecer
 * uma categoria em si, quando é justamente a ausência de desvio.
 */
export const diverging = {
  negative: "#B91C1C",
  midpoint: "#94A3B8",
  positive: "#15803D",
};

/**
 * Medidas dos traços.
 *
 * Marca fina e eixo discreto: o dado tem que pesar mais que a moldura. O
 * espaçador de 2px entre áreas empilhadas existe para a fronteira ser lida
 * como fronteira, e não como mais uma faixa de cor.
 */
export const marks = {
  lineWidth: 2,
  dotRadius: 4,
  activeDotRadius: 6,
  barRadius: [4, 4, 0, 0],
  barGapPx: 2,
  gridOpacity: 0.6,
};

/** Tokens que dependem do tema — grid, eixo e fundo do tooltip. */
export const chartTheme = (theme) => ({
  grid: theme.palette.divider,
  axis: theme.palette.text.secondary,
  axisTick: { fill: theme.palette.text.secondary, fontSize: 11 },
  tooltip: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    boxShadow: theme.shadows[3],
    fontSize: 12,
    color: theme.palette.text.primary,
  },
  // Texto sempre com token de texto, nunca com a cor da série: um rótulo
  // pintado da cor da linha compete com a própria linha pela atenção.
  label: theme.palette.text.primary,
  mutedLabel: theme.palette.text.secondary,
});

/** Cor da série pela posição, sem ciclar. */
export const seriesColor = (index) =>
  categorical[index] || status.neutral;

export default categorical;
